import { useEffect, useCallback, useState, memo } from 'react';
import { Accordion, Box, Flex, Spinner } from '@chakra-ui/react';

import './index.css';
import { GradientSpinner } from '@/components/ai/GradientSpinner';
import { StepAccordion } from './StepAccordian';
import { useGetAllFlowPreviewsMutation } from '@/store/query/chatgpt';
import { useRetry } from '@/hooks/use-retry';
import { getIsActionRequired, getIsCompleted, getIsSkipped } from './utils';
import { KText } from '@/ui-library/components/text';

const FlowPreview = ({ flow = {}, contactId }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [flowPreview] = useGetAllFlowPreviewsMutation();
  const [steps, setSteps] = useState(undefined);
  const [isPreviewLoading, setPreviewLoading] = useState(false);
  const [startFlowPreview, setStartFlowPreview] = useState(false);

  const moveNextStep = useCallback((e) => {
    //Save API;
    setActiveIndex((prevIndex) => prevIndex + 1);
    e.stopPropagation();
  }, []);

  const handleAccordion = (e, index) => {
    setActiveIndex(index);
    e.stopPropagation();
  };

  useEffect(() => {
    if (!flow?.id) return;
    setStartFlowPreview(false);
    setPreviewLoading(false);
    setSteps(undefined);
  }, [flow]);

  useEffect(() => {
    if (!startFlowPreview) {
      setStartFlowPreview(true);
      setPreviewLoading(true);
    }
  }, [startFlowPreview]);

  const getFlowPreviewData = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await flowPreview({
          contactId: contactId,
          outreachId: flow?.id,
        });
        const resData = res?.data || [];

        if (
          // Only stop polling if all steps has the ai steps generated / failed or if no AI step is present
          resData
            ?.filter((step) => step?.personalisation)
            ?.every(
              (step) =>
                step?.ai_messages?.[0]?.status === 'success' ||
                step?.ai_messages?.[0]?.status === 'failed'
            ) ||
          resData?.filter((step) => step?.personalisation)?.length === 0
        ) {
          resolve(resData);
        } else {
          // Reject and continue polling, but update the state, to show the step view instead of loading screen
          reject(resData);
          setSteps(resData);
        }
      } catch (error) {
        reject(error);
      }
    });
  };

  const {
    inProgress,
    data: flowPreviewDataState,
    error: flowPreviewErrorState,
  } = useRetry({
    fn: getFlowPreviewData,
    args: {},
    retryOptions: { factor: 2, retries: 10, maxTimeout: 5000 },
    start: startFlowPreview,
  });

  useEffect(() => {
    if (
      inProgress ||
      (flowPreviewDataState === null && flowPreviewErrorState === null)
    ) {
      return;
    }

    if (flowPreviewDataState) {
      setSteps(flowPreviewDataState);
      setPreviewLoading(false);
      const stage = flowPreviewDataState?.[0]?.step_candidate?.stage;
      if (
        getIsActionRequired(stage) ||
        getIsCompleted(stage) ||
        getIsSkipped(stage)
      ) {
        setActiveIndex(-1);
      }
    }

    if (flowPreviewErrorState) {
      setPreviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, flowPreviewDataState, flowPreviewErrorState]);

  if (
    isPreviewLoading &&
    steps?.filter((step) => step?.personalisation)?.length > 0 &&
    steps
      ?.filter((step) => step?.personalisation)
      ?.every((step) => step?.ai_messages?.[0]?.status !== 'success')
  ) {
    return (
      <Flex
        minH="500px"
        justifyContent="center"
        alignItems="center"
        flexDir="column"
        gap="12px"
      >
        <GradientSpinner />
        <KText size="sm-semi-bold">Working on it!</KText>
        <KText w="238px" textAlign="center">
          Preview of your AI-generated messages will be displayed here when they
          are ready.
        </KText>
      </Flex>
    );
  }

  return (
    <Box bg="#FFF" paddingTop="10px">
      <Accordion allowToggle index={activeIndex}>
        {steps === undefined && (
          <Flex justifyContent="center">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="#F2F4F7"
              color="#444CE7"
              size="lg"
            />
          </Flex>
        )}
        {steps === undefined || steps?.length > 0 ? (
          steps?.map((step, index) => (
            <StepAccordion
              step={step}
              index={index}
              activeIndex={activeIndex}
              moveNextStep={moveNextStep}
              handleAccordion={handleAccordion}
              flow={flow}
              steps={steps}
              contactId={contactId}
            />
          ))
        ) : (
          <KText align="center" mt="10px">
            No steps found.
          </KText>
        )}
      </Accordion>
    </Box>
  );
};

export default memo(FlowPreview);
